import axios from "axios";
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import swal from "sweetalert";
import { getProductImageUrl, handleProductImageError } from "../../../util/storeLogoHelper";
import {
  FaArrowLeft,
  FaFolderOpen,
  FaSave,
  FaImage,
  FaCloudUploadAlt,
  FaTrashAlt,
  FaCheckCircle,
  FaEye,
  FaLayerGroup,
  FaRedo,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaBoxes
} from "react-icons/fa";
import ShopifySEOEditor from "../common/ShopifySEOEditor";
import { checkImageFile, focusFirstError, hasApiErrors, readApiErrors } from "../../../util/formErrors";

export default function EditCategory(props) {
  const history = useHistory();
  const categoryId = props.match?.params?.id;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [categoryInput, setCategoryInput] = useState({
    id: "",
    name: "",
    slug: "",
    description: "",
    status: 1, // 1 = active/visible, 0 = hidden
    meta_title: "",
    meta_keyword: "",
    meta_descrip: "",
    image: "",
  });

  const [newPicture, setNewPicture] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState(null);
  const [isCustomSlug, setIsCustomSlug] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const slugify = (text) => {
    return (text || "")
      .toLowerCase()
      .trim()
      .replace(/[\s\W-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  useEffect(() => {
    document.title = "Edit Category — Admin Management";

    if (!categoryId) {
      swal("Error", "Invalid Category ID", "error");
      history.push("/admin/view-category");
      return;
    }

    axios
      .get(`/api/edit-category/${categoryId}`)
      .then((res) => {
        setLoading(false);
        if (res.data.status === 200 && res.data.category) {
          const cat = res.data.category;
          setCategoryInput({
            id: cat.id || categoryId,
            name: cat.name || "",
            slug: cat.slug || "",
            description: cat.description || "",
            status: (Number(cat.status) === 1 || cat.status === "1" || cat.status === true) ? 1 : 0,
            meta_title: cat.meta_title || "",
            meta_keyword: cat.meta_keyword || "",
            meta_descrip: cat.meta_descrip || "",
            image: cat.image || "",
          });
        } else if (res.data.status === 205) {
          swal("Access Denied", res.data.message || "Unauthorized", "error");
          history.push("/admin/view-category");
        } else {
          swal("Error", res.data.message || "Category not found", "error");
          history.push("/admin/view-category");
        }
      })
      .catch(() => {
        setLoading(false);
        swal("Error", "Could not fetch category details", "error");
        history.push("/admin/view-category");
      });
  }, [categoryId, history]);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setCategoryInput((prev) => {
      const updated = { ...prev, [name]: value };
      // Auto-sync slug if user hasn't explicitly customized it
      if (name === "name" && !isCustomSlug) {
        updated.slug = slugify(value);
      }
      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSlugChange = (e) => {
    setCategoryInput((prev) => ({ ...prev, slug: slugify(e.target.value) }));
  };

  const handleStatusToggle = (e) => {
    // checked = true (Visible / Status 1), unchecked = false (Hidden / Status 0)
    setCategoryInput((prev) => ({
      ...prev,
      status: e.target.checked ? 1 : 0,
    }));
  };

  const processImageFile = (file) => {
    if (!file) return;
    // Same rules as the server, shown under the photo box.
    const problem = checkImageFile(file);
    setErrors((prev) => ({ ...prev, image: problem }));
    if (problem) return;

    setNewPicture(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setNewImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processImageFile(e.target.files[0]);
    }
    e.target.value = "";
  };

  const removeNewImage = () => {
    setNewPicture(null);
    setNewImagePreview(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processImageFile(e.dataTransfer.files[0]);
    }
  };

  const updateCategory = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});

    const finalSlug = categoryInput.slug || slugify(categoryInput.name);
    const finalMetaTitle = categoryInput.meta_title || categoryInput.name;

    const formData = new FormData();
    formData.append("name", categoryInput.name || "");
    formData.append("slug", finalSlug);
    formData.append("description", categoryInput.description || "");
    formData.append("status", (Number(categoryInput.status) === 1 || categoryInput.status === "1" || categoryInput.status === true) ? "1" : "0");
    formData.append("meta_title", finalMetaTitle);
    formData.append("meta_keyword", categoryInput.meta_keyword || "");
    formData.append("meta_descrip", categoryInput.meta_descrip || "");

    if (newPicture) {
      formData.append("image", newPicture);
    }

    // Errors arrive as a 200 reply with status 4xx, or as an HTTP error: show both under the fields.
    const showErrors = (source) => {
      const { message, fields } = readApiErrors(source);
      setErrors(fields);
      focusFirstError();
      swal("Category not saved", message || "Please check the highlighted fields.", "error");
    };
    axios
      .post(`/api/update-category/${categoryId}`, formData)
      .then((res) => {
        setSubmitting(false);
        if (res.data.status === 200) {
          swal("Success", res.data.message || "Category updated successfully", "success");
          history.push("/admin/view-category");
        } else if (res.data.status === 404) {
          swal("Error", res.data.message || "Category not found", "error");
          history.push("/admin/view-category");
        } else if (hasApiErrors(res)) {
          showErrors(res);
        }
      })
      .catch((err) => {
        setSubmitting(false);
        showErrors(err);
      });
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status"></div>
          <p className="mt-3 text-muted fw-semibold" style={{ fontSize: "14px" }}>
            Loading category data...
          </p>
        </div>
      </div>
    );
  }

  const isVisible = Number(categoryInput.status) === 1;
  const effectiveSlug = categoryInput.slug || slugify(categoryInput.name) || "category-slug";

  return (
    <div className="container-fluid px-0">
      {/* Top Header Bar */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-1 small">
              <li className="breadcrumb-item">
                <Link to="/admin/dashboard" className="text-decoration-none text-muted">
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/admin/view-category" className="text-decoration-none text-muted">
                  Categories
                </Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                Edit #{categoryInput.id}
              </li>
            </ol>
          </nav>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Edit Category: <span className="text-primary">{categoryInput.name || `#${categoryInput.id}`}</span>
            </h3>
            <span className="admin-badge admin-badge-info">
              <FaLayerGroup size={11} className="me-1" /> Category ID: #{categoryInput.id}
            </span>
            <span
              className={`badge rounded-pill ${
                isVisible ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25" : "bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25"
              } px-3 py-1`}
              style={{ fontSize: "11.5px", fontWeight: "600" }}
            >
              {isVisible ? "● Active & Visible" : "○ Hidden / Inactive"}
            </span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/view-category"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaArrowLeft size={13} /> Discard
          </Link>
          <button
            type="button"
            onClick={updateCategory}
            disabled={submitting}
            className="btn btn-primary d-inline-flex align-items-center gap-2 fw-semibold px-4 py-2 shadow-sm rounded-3"
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span>Updating...</span>
              </>
            ) : (
              <>
                <FaSave size={14} /> Update Category
              </>
            )}
          </button>
        </div>
      </div>

      <form onSubmit={updateCategory} id="EDIT_CATEGORY_FORM">
        <div className="row g-4">
          {/* Left Column: Primary Category Info */}
          <div className="col-12 col-lg-8">
            {/* Category Details Card */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FaFolderOpen className="text-primary" />
                  <h5 className="admin-card-title mb-0">Category Information</h5>
                </div>
                <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                  Record #{categoryInput.id}
                </span>
              </div>
              <div className="admin-card-body p-4">
                {/* Category Name */}
                <div className="mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="e.g. Women's Handbags, Winter Jackets"
                    className={`form-control form-control-lg ${errors?.name ? "is-invalid" : ""}`}
                    onChange={handleInput}
                    value={categoryInput.name}
                    required
                  />
                  {errors?.name && <div className="invalid-feedback">{errors.name}</div>}
                </div>

                {/* Slug Handle */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-semibold text-dark mb-0 small">
                      URL Slug Handle <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newSlug = slugify(categoryInput.name);
                          setCategoryInput((prev) => ({ ...prev, slug: newSlug }));
                        }}
                        className="btn btn-link btn-sm text-decoration-none p-0 text-muted"
                        style={{ fontSize: "12px" }}
                        title="Re-generate slug from current name"
                      >
                        <FaRedo size={10} className="me-1" /> Sync Name
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsCustomSlug(!isCustomSlug)}
                        className="btn btn-link btn-sm text-decoration-none p-0"
                        style={{ fontSize: "12px" }}
                      >
                        {isCustomSlug ? "Lock Slug" : "Edit URL"}
                      </button>
                    </div>
                  </div>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-muted small font-monospace">
                      /collections/
                    </span>
                    <input
                      type="text"
                      name="slug"
                      disabled={!isCustomSlug}
                      placeholder="womens-handbags"
                      className={`form-control font-monospace ${errors?.slug ? "is-invalid" : ""}`}
                      onChange={handleSlugChange}
                      value={categoryInput.slug}
                      required
                    />
                  </div>
                  {errors?.slug && <small className="text-danger d-block mt-1">{errors.slug}</small>}
                  <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>
                    The “slug” is the canonical URL path for this collection.
                  </small>
                </div>

                {/* Description */}
                <div className="mb-0">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <label className="form-label fw-semibold text-dark mb-0">Description</label>
                    <span className="text-muted small" style={{ fontSize: "11.5px" }}>
                      {(categoryInput.description || "").length} characters
                    </span>
                  </div>
                  <textarea
                    name="description"
                    rows={4}
                    placeholder="Add a detailed description for this category to improve discovery..."
                    className="form-control"
                    onChange={handleInput}
                    value={categoryInput.description || ""}
                  />
                  <small className="text-muted d-block mt-1" style={{ fontSize: "11.5px" }}>
                    This description will be displayed on the category collection page.
                  </small>
                </div>
              </div>
            </div>

            {/* Category Banner Image Card */}
            <div className="admin-card mb-4 shadow-sm border-0">
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <FaImage className="text-primary" />
                  <h5 className="admin-card-title mb-0">Category Image / Banner</h5>
                </div>
                <span className="badge bg-light text-muted border px-2 py-1" style={{ fontSize: "11px" }}>
                  {categoryInput.image ? "Banner Configured" : "No Banner"}
                </span>
              </div>
              <div className="admin-card-body p-4">
                {/* Current Active Banner Preview */}
                {categoryInput.image && !newImagePreview && (
                  <div className="mb-3 p-3 bg-light rounded-3 border">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold text-dark small d-flex align-items-center gap-1">
                        <FaCheckCircle className="text-success" size={13} /> Active Banner on Storefront
                      </span>
                      <span className="text-muted small" style={{ fontSize: "11px" }}>
                        Saved in: {categoryInput.image}
                      </span>
                    </div>
                    <div
                      style={{
                        height: "180px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={getProductImageUrl(categoryInput.image)}
                        alt={categoryInput.name}
                        onError={handleProductImageError}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          backgroundColor: "#fff",
                        }}
                      />
                    </div>
                    <div className="mt-2 text-muted small" style={{ fontSize: "12px" }}>
                      Upload a new image below if you would like to replace this active banner.
                    </div>
                  </div>
                )}

                {/* Newly Picked Image Preview (Ready to upload) */}
                {newImagePreview ? (
                  <div className="mb-3 p-3 rounded-3 border border-primary bg-primary bg-opacity-10 position-relative">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-semibold text-primary small d-flex align-items-center gap-1">
                        <FaCheckCircle size={13} /> New Banner Selected (Ready to Save)
                      </span>
                      <button
                        type="button"
                        onClick={removeNewImage}
                        className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 py-1 px-2"
                        style={{ fontSize: "12px" }}
                      >
                        <FaTrashAlt size={11} /> Cancel & Keep Current Banner
                      </button>
                    </div>

                    <div
                      style={{
                        height: "180px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        border: "1px solid #cbd5e1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        src={newImagePreview}
                        alt="New Banner Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-2 text-muted small" style={{ fontSize: "11.5px" }}>
                      <span><strong>File:</strong> {newPicture?.name}</span>
                      <span><strong>Size:</strong> {(newPicture?.size / 1024).toFixed(1)} KB</span>
                    </div>
                  </div>
                ) : (
                  /* Drag and drop upload zone */
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`text-center p-4 rounded-3 border border-2 border-dashed ${
                      isDragging ? "border-primary bg-primary bg-opacity-10" : "border-secondary border-opacity-25 bg-light"
                    }`}
                    style={{ transition: "all 0.2s ease" }}
                  >
                    <FaCloudUploadAlt className="text-primary mb-2" size={38} />
                    <h6 className="fw-semibold text-dark mb-1" style={{ fontSize: "14px" }}>
                      {categoryInput.image ? "Click to replace or drop new banner" : "Click to upload category banner"}
                    </h6>
                    <p className="text-muted mb-3" style={{ fontSize: "12px" }}>
                      JPG, PNG, WebP or GIF (recommended 1200 × 400px, max 5 MB)
                    </p>
                    <label className="btn btn-outline-primary btn-sm px-3 fw-semibold rounded-pill cursor-pointer">
                      Browse Files
                      <input
                        type="file"
                        name="image"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="d-none"
                        onChange={handleImageChange}
                      />
                    </label>
                  </div>
                )}
                {errors?.image && (
                  <div className="field-error alert alert-danger py-2 px-3 mt-2 mb-0 small" role="alert">{errors.image}</div>
                )}
              </div>
            </div>

            {/* Shopify-Style Search Engine Optimization (SEO) */}
            <ShopifySEOEditor
              itemName={categoryInput.name}
              itemCategoryName={categoryInput.name}
              itemDescription={categoryInput.descrip}
              itemSlug={categoryInput.slug}
              metaTitle={categoryInput.meta_title}
              metaDescription={categoryInput.meta_descrip}
              metaKeywords={categoryInput.meta_keyword}
              type="category"
              onChange={(updated) => {
                setCategoryInput((prev) => ({
                  ...prev,
                  meta_title: updated.meta_title !== undefined ? updated.meta_title : prev.meta_title,
                  meta_descrip: updated.meta_descrip !== undefined ? updated.meta_descrip : prev.meta_descrip,
                  meta_keyword: updated.meta_keyword !== undefined ? updated.meta_keyword : prev.meta_keyword,
                }));
              }}
            />
          </div>

          {/* Right Column: Publishing Status & Merchandising Sidebar */}
          <div className="col-12 col-lg-4">
            {/* Publishing Status Card */}
            <div className="admin-card mb-4 shadow-sm border-0 sticky-top" style={{ top: "85px" }}>
              <div className="admin-card-header bg-white py-3 border-bottom d-flex align-items-center gap-2">
                <FaEye className="text-primary" />
                <h5 className="admin-card-title mb-0">Publishing Status</h5>
              </div>
              <div className="admin-card-body p-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <label className="form-label fw-semibold text-dark mb-0">Store Visibility</label>
                    <div className="text-muted small" style={{ fontSize: "12px" }}>
                      {isVisible ? "Visible to customers in store (Active / Shown)" : "Hidden from customer storefront (Hidden)"}
                    </div>
                  </div>
                  <div className="form-check form-switch m-0">
                    <input
                      className="form-check-input cursor-pointer"
                      type="checkbox"
                      role="switch"
                      id="statusSwitch"
                      checked={isVisible}
                      onChange={handleStatusToggle}
                      style={{ width: "2.5em", height: "1.25em" }}
                    />
                  </div>
                </div>

                <div
                  className={`p-3 rounded-3 d-flex align-items-center gap-2 ${
                    isVisible ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25" : "bg-warning bg-opacity-10 text-dark border border-warning border-opacity-25"
                  }`}
                  style={{ fontSize: "13px" }}
                >
                  <span className="fw-semibold">Status:</span>
                  <span>{isVisible ? "Active & Visible in Catalog" : "Draft / Hidden from Customers"}</span>
                </div>
              </div>

              {/* Quick Actions & Storefront Link */}
              <div className="p-4 border-top bg-light bg-opacity-50">
                <h6 className="fw-semibold text-dark mb-2" style={{ fontSize: "13px" }}>
                  Quick Shortcuts
                </h6>
                <div className="d-flex flex-column gap-2">
                  <Link
                    to="/admin/view-product"
                    className="btn btn-sm btn-white border bg-white d-flex align-items-center justify-content-between text-dark text-decoration-none py-2 px-3 rounded-2 shadow-xs"
                    style={{ fontSize: "12.5px" }}
                  >
                    <span className="d-flex align-items-center gap-2">
                      <FaBoxes className="text-primary" /> View Catalog Products
                    </span>
                    <FaExternalLinkAlt size={10} className="text-muted" />
                  </Link>

                  <a
                    href={`/collections/${effectiveSlug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-white border bg-white d-flex align-items-center justify-content-between text-dark text-decoration-none py-2 px-3 rounded-2 shadow-xs"
                    style={{ fontSize: "12.5px" }}
                  >
                    <span className="d-flex align-items-center gap-2">
                      <FaEye className="text-info" /> View on Live Store
                    </span>
                    <FaExternalLinkAlt size={10} className="text-muted" />
                  </a>
                </div>
              </div>

              {/* Merchandising Tips */}
              <div className="p-4 border-top">
                <div className="d-flex align-items-center gap-2 text-warning mb-2">
                  <FaInfoCircle />
                  <h6 className="fw-semibold text-dark mb-0" style={{ fontSize: "13px" }}>
                    Merchandising Tips
                  </h6>
                </div>
                <ul className="text-muted small ps-3 mb-0" style={{ fontSize: "12px", lineHeight: "1.6" }}>
                  <li className="mb-1">
                    <strong>Descriptive Names:</strong> Keep names concise and familiar to customers.
                  </li>
                  <li className="mb-1">
                    <strong>Banner Imagery:</strong> Clean horizontal banners enhance collection landing pages.
                  </li>
                  <li>
                    <strong>Search SEO:</strong> Relevant meta descriptions boost search discovery and CTR.
                  </li>
                </ul>
              </div>

              {/* Actions Footer */}
              <div className="p-4 border-top bg-white">
                <button
                  type="button"
                  onClick={updateCategory}
                  disabled={submitting}
                  className="btn btn-primary w-100 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2 shadow-sm rounded-3 mb-2"
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span>Saving Changes...</span>
                    </>
                  ) : (
                    <>
                      <FaSave size={14} /> Update Category
                    </>
                  )}
                </button>
                <Link
                  to="/admin/view-category"
                  className="btn btn-outline-secondary w-100 py-2 fw-semibold rounded-3"
                  style={{ fontSize: "13.5px" }}
                >
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

